import React from 'react';
import { useInstagram } from '../utils/instagram';

/**
 * Marquee ticker — full-width auto-scrolling strip of the latest posts.
 * Never stops moving; pauses on hover. Uses CSS keyframes (no JS animation loop).
 */
const InstagramMarquee = () => {
  const { items, configured, loading } = useInstagram();

  if (loading || !configured || items.length === 0) return null;

  // Show every post Behold returns (up to a sane cap so a huge feed doesn't
  // create an absurdly long DOM). Reel Wall above stays at 6.
  const tiles = items.slice(0, 30);
  // Duplicate the array so the marquee loops seamlessly
  const looped = [...tiles, ...tiles];

  // Keep perceived scroll speed roughly constant regardless of tile count:
  // ~4 seconds per unique tile feels right for readability.
  const durationSec = Math.max(30, tiles.length * 4);

  return (
    <section
      className="py-14 md:py-16 bg-[#FBF6EC] overflow-hidden"
      data-testid="instagram-marquee"
      aria-label="Latest posts from @bricklanela on Instagram"
    >
      <div className="text-center mb-8 px-6">
        <p
          className="text-[10px] tracking-[0.45em] uppercase text-[#C9A86C] mb-2"
          style={{ fontFamily: 'var(--font-serif)' }}
        >
          Live · @bricklanela
        </p>
        <h2
          className="text-3xl md:text-4xl"
          style={{ fontFamily: 'var(--font-serif)' }}
        >
          Straight from the Arts District
        </h2>
      </div>

      <div
        className="flex gap-3 w-max hover:[animation-play-state:paused]"
        style={{
          animationName: 'bl-marquee',
          animationDuration: `${durationSec}s`,
          animationTimingFunction: 'linear',
          animationIterationCount: 'infinite',
        }}
        data-testid="marquee-track"
      >
        {looped.map((p, i) => (
          <a
            key={`${p.id}-${i}`}
            href={p.permalink}
            target="_blank"
            rel="noopener noreferrer"
            className="relative w-48 h-48 md:w-64 md:h-64 flex-shrink-0 overflow-hidden group"
            data-testid={`marquee-tile-${i}`}
          >
            {p.media_type === 'VIDEO' ? (
              <video
                src={p.media_url}
                poster={p.thumbnail_url}
                className="w-full h-full object-cover"
                muted
                loop
                playsInline
                preload="metadata"
              />
            ) : (
              <img
                src={p.thumbnail_url || p.media_url}
                alt={p.caption || 'Brick Lane on Instagram'}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                loading="lazy"
              />
            )}
          </a>
        ))}
      </div>

      <style>{`
        @keyframes bl-marquee {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }
      `}</style>
    </section>
  );
};

export default InstagramMarquee;
